import { Easing, interpolate } from 'remotion';
import { SegmentBoundaryTransition } from '../../../MainPlayer/types/Track/SegmentTransition';
import type { TransitionPropertiesWithDip } from './types';

export const getEasingFunction = (easing?: 'linear' | 'easeInOut') => {
  switch (easing) {
    case 'linear':
      return Easing.linear;
    case 'easeInOut':
    default:
      return Easing.inOut(Easing.ease);
  }
};

export const createSegmentTransitionMap = (
  segmentTransitions?: SegmentBoundaryTransition[]
): Map<string, { outgoing?: SegmentBoundaryTransition; incoming?: SegmentBoundaryTransition }> => {
  const segmentTransitionMap = new Map<
    string,
    { outgoing?: SegmentBoundaryTransition; incoming?: SegmentBoundaryTransition }
  >();

  if (!segmentTransitions) {
    return segmentTransitionMap;
  }

  segmentTransitions.forEach(transition => {
    if (transition.fromSegmentId) {
      const existing = segmentTransitionMap.get(transition.fromSegmentId) || {};
      segmentTransitionMap.set(transition.fromSegmentId, { ...existing, outgoing: transition });
    }
    if (transition.toSegmentId) {
      const existing = segmentTransitionMap.get(transition.toSegmentId) || {};
      segmentTransitionMap.set(transition.toSegmentId, { ...existing, incoming: transition });
    }
  });

  return segmentTransitionMap;
};

export function hasAudioDip(transition: { properties?: unknown } | null | undefined): boolean {
  return (transition?.properties as TransitionPropertiesWithDip | undefined)?.audioDip === true;
}

/**
 * Returns a volume multiplier (0–1) for segment transition audio dip.
 * Matches visual transition timing: incoming fades 0→1 at segment start, outgoing fades 1→0 at segment end.
 */
export function getSegmentTransitionVolumeMultiplier(
  segmentRelativeFrame: number,
  segmentDurationInFrames: number,
  incomingTransition?: SegmentBoundaryTransition | null,
  outgoingTransition?: SegmentBoundaryTransition | null
): number {
  const propsIn = incomingTransition?.properties as TransitionPropertiesWithDip | undefined;
  const propsOut = outgoingTransition?.properties as TransitionPropertiesWithDip | undefined;
  const easingIn = getEasingFunction(propsIn?.easing);
  const easingOut = getEasingFunction(propsOut?.easing);

  let multiplier = 1;

  if (propsIn?.audioDip && incomingTransition?.inDurationInFrames) {
    const fadeInEnd = incomingTransition.inDurationInFrames;
    if (segmentRelativeFrame < fadeInEnd) {
      multiplier *= interpolate(segmentRelativeFrame, [0, fadeInEnd], [0, 1], {
        easing: easingIn,
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp'
      });
    }
  }

  if (propsOut?.audioDip && outgoingTransition?.outDurationInFrames) {
    const fadeOutStart = segmentDurationInFrames - outgoingTransition.outDurationInFrames;
    if (segmentRelativeFrame >= fadeOutStart) {
      multiplier *= interpolate(segmentRelativeFrame, [fadeOutStart, segmentDurationInFrames], [1, 0], {
        easing: easingOut,
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp'
      });
    }
  }

  return multiplier;
}
